import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { ContainerFlow, YardMoveCategory } from '@/api/generated.schemas';
import { SearchableMultiSelect } from '@/components/blocks/searchable-multi-select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { formatVisitLabel } from '@/routes/(app)/(scenarios)/(simulation)/components/simulation-yard-moves-tooltip';
import { FLOW_KEYS } from '@/routes/(app)/(scenarios)/(simulation)/yard-moves.utils';
import type { YardMovesChartFilters } from '@/routes/(app)/(scenarios)/(simulation)/yard-moves-chart.utils';

function withMemberToggled<T>(set: Set<T>, value: T, on: boolean): Set<T> {
  const next = new Set(set);
  if (on) next.add(value);
  else next.delete(value);
  return next;
}

interface CheckboxGroupProps<T extends string> {
  title: string;
  values: Array<T>;
  checked: Set<T>;
  labelFor: (value: T) => string;
  onChange: (next: Set<T>) => void;
}

const CheckboxGroup = <T extends string>({
  title,
  values,
  checked,
  labelFor,
  onChange,
}: CheckboxGroupProps<T>) => (
  <div className='flex items-center gap-3'>
    <span className='text-muted-foreground text-xs font-medium'>{title}</span>
    {values.map((value) => (
      <Label
        key={value}
        className='cursor-pointer text-xs'>
        <Checkbox
          checked={checked.has(value)}
          onCheckedChange={(state) => {
            onChange(withMemberToggled(checked, value, state === true));
          }}
        />
        {labelFor(value)}
      </Label>
    ))}
  </div>
);

interface SimulationYardMovesFilterBarProps {
  filters: YardMovesChartFilters;
  onChange: (patch: Partial<YardMovesChartFilters>) => void;
  visitOptions: Array<{ id: string; vesselName?: string | null }>;
}

const SimulationYardMovesFilterBarInner: FC<
  SimulationYardMovesFilterBarProps
> = ({ filters, onChange, visitOptions }) => {
  const { t } = useTranslation();
  const { selectedVisitIds } = filters;

  return (
    <div className='flex flex-wrap items-center gap-x-6 gap-y-2'>
      <CheckboxGroup
        title={t('simulation.charts.yardMoves.filters.categories')}
        values={Object.values(YardMoveCategory)}
        checked={filters.categories}
        labelFor={(value) => t(`simulation.charts.yardMoves.panels.${value}`)}
        onChange={(categories) => {
          onChange({ categories });
        }}
      />
      <CheckboxGroup
        title={t('simulation.charts.yardMoves.filters.containerFlows')}
        values={Object.values(ContainerFlow)}
        checked={filters.containerFlows}
        labelFor={(value) => t(FLOW_KEYS[value])}
        onChange={(containerFlows) => {
          onChange({ containerFlows });
        }}
      />
      <SearchableMultiSelect
        options={visitOptions.map(({ id, vesselName }) => ({
          value: id,
          label: formatVisitLabel(t, id, vesselName),
          keywords: id,
        }))}
        selected={selectedVisitIds}
        onChange={(next) => {
          onChange({ selectedVisitIds: next });
        }}
        triggerLabel={
          selectedVisitIds.length > 0
            ? t('simulation.charts.yardMoves.filters.visitsSelected', {
                total: selectedVisitIds.length,
              })
            : t('simulation.charts.yardMoves.filters.visits')
        }
        searchPlaceholder={t(
          'simulation.charts.yardMoves.filters.searchVisits'
        )}
        emptyText={t('simulation.charts.yardMoves.filters.noVisits')}
        clearText={t('simulation.charts.yardMoves.filters.clear')}
      />
      <Label className='cursor-pointer text-xs'>
        <Switch
          checked={filters.showSelectedOnly && selectedVisitIds.length > 0}
          disabled={selectedVisitIds.length === 0}
          onCheckedChange={(showSelectedOnly) => {
            onChange({ showSelectedOnly });
          }}
        />
        {t('simulation.charts.yardMoves.filters.showSelectedOnly')}
      </Label>
    </div>
  );
};

export const SimulationYardMovesFilterBar = memo(
  SimulationYardMovesFilterBarInner
);
